import { api } from '@/lib/api-client'
import { logger } from '@/lib/logger'
import { PaymentContext, PaymentEventType, paymentTelemetry } from '@/lib/telemetry/payment-telemetry'
import { Cart, CartError, CartItem, CartItemLegacy, UpdateCartPaymentMethodRequest } from '@/types/commerce-types'
import { PagedResponse, Result } from '@/types/core-types'

// =============================================================================
// Cart API Types
// =============================================================================

/**
 * Request to create a new cart.
 * POST /api/v2/cart
 */
export interface CreateCartRequest {
  /** Device/browser session ID for cart persistence across login/logout */
  sessionId?: string
}

/**
 * Response from cart creation endpoint.
 */
export interface CartKeyResponse {
  cartKey: string
  cartId: number
}

/**
 * Request for adding a Skyward food service payment to the cart.
 * POST /api/v2/cart/skyward-food
 */
export interface AddSkywardFoodItemRequest {
  cartKey: string
  /** Device/browser session ID - enables auto-create if cart not found */
  sessionId?: string
  amount: number
  studentId: number
  studentName?: string
  schoolId: number
  recurringFrequency?: 'one-time' | 'weekly' | 'bi-weekly' | 'monthly' | 'low-balance'
  firstRecurDate?: string
  lowBalanceThreshold?: number
  foodPayorId?: string
}

/**
 * Request for adding a Skyward student fee to the cart.
 * POST /api/v2/cart/skyward-fees
 */
export interface AddSkywardFeeItemRequest {
  cartKey: string
  /** Device/browser session ID - enables auto-create if cart not found */
  sessionId?: string
  feeId: string
  feeBcId?: string
  name: string
  description?: string
  amount: number
  studentId: number
  studentName?: string
  schoolId: number
  recurringFrequency?: 'one-time' | 'weekly' | 'bi-weekly' | 'monthly'
  firstRecurDate?: string
}

/**
 * Request for adding a standard merchandise item to the cart.
 * POST /api/v2/cart/items
 */
export interface AddStandardItemRequest {
  cartKey: string
  /** Device/browser session ID - enables auto-create if cart not found */
  sessionId?: string
  itemId: number
  qty?: number
  amount?: number
  schoolId?: number
  schoolCorpId?: number
  myStudentId?: number
  restrictionId?: number
  promos?: PromoCodeRequest[]
  customData?: CustomDataRequest
}

/**
 * Request for adding an event ticket to the cart.
 * POST /api/v2/cart/events
 */
export interface AddEventItemRequest {
  cartKey: string
  /** Device/browser session ID - enables auto-create if cart not found */
  sessionId?: string
  itemId: number
  qty?: number
  seatId?: number
  itemType: 4 | 5 | 6 // 4=GeneralAdmission, 5=ReservedSeating, 6=EventPass
  schoolId?: number
  schoolCorpId?: number
  myStudentId?: number
  renew?: boolean
  oldPassCartItemId?: number
  restrictionId?: number
  amount?: number
  promos?: PromoCodeRequest[]
  customData?: CustomDataRequest
}

/**
 * Request for adding a variable-price item to the cart.
 * POST /api/v2/cart/variable-pricing
 */
export interface AddVariablePricingItemRequest {
  cartKey: string
  /** Device/browser session ID - enables auto-create if cart not found */
  sessionId?: string
  itemId: number
  amount: number
  qty?: number
  schoolId?: number
  schoolCorpId?: number
  myStudentId?: number
  restrictionId?: number
  promos?: PromoCodeRequest[]
}

/**
 * Promotional code discount for cart items.
 */
export interface PromoCodeRequest {
  id: number
  code: string
  amount: number
  isPercentage: boolean
}

/**
 * Custom field data for cart items.
 */
export interface CustomDataRequest {
  fields?: CustomFieldAnswerRequest[]
  addresses?: AddressAnswerRequest[]
  students?: StudentAnswerRequest[]
  guardians?: GuardianAnswerRequest[]
  medicals?: MedicalAnswerRequest[]
}

export interface CustomFieldAnswerRequest {
  id: number
  fieldType: number
  value?: string
  values?: string[]
}

export interface AddressAnswerRequest {
  id: number
  fieldType: number
  adr1?: string
  adr2?: string
  city?: string
  state?: string
  zip?: string
}

export interface StudentAnswerRequest {
  id: number
  fieldType: number
  fName?: string
  lName?: string
  studentId?: string
  grade?: string
}

export interface GuardianAnswerRequest {
  id: number
  fieldType: number
  fName?: string
  lName?: string
  phone?: string
  email?: string
}

export interface MedicalAnswerRequest {
  id: number
  fieldType: number
  fName?: string
  lName?: string
  phone?: string
  physician?: string
  physicianPhone?: string
  conditions?: string
}

/**
 * Response from cart add item endpoints.
 * Note: API returns `id` field (from CartItemModel.Id in .NET API)
 */
export interface AddCartItemResponse {
  id: number
  cartKey: string
  itemId: number | null
  itemType: string
  itemTypeId: number
  name: string
  description?: string | null
  image?: string | null
  amount: number
  itemAmount: number
  qty: number
  fee: number
  discount: number
  tax: number
  taxPercentage?: number | null
  taxable: boolean
  subtotal: number
  cartId: number
  tenantId: number
  schoolId?: number | null
  departmentId?: number | null
  meta?: Record<string, unknown>
}

export interface PaymentToken {
  sessionTokenId: string
  requestId: string
  expiresIn: number
  createdAt: Date
  token: string
  secret: string
  credentialId: string
  endpoint: string
  payerId: string
}

// Legacy ProPay token structure - used in checkout-form.tsx
export interface LegacyPaymentToken {
  token: string
  payerId: string
  endpoint: string
  credentialId: number
  secret: string
}

export interface Order {
  id: string
  orderDate: string
  amount: number
  purchaserName: string
  paymentInfo: string
  departments: string[]
  categories: string[]
  canRefund: boolean
  publicId?: string
  isRefunded: boolean
  isPartiallyRefunded: boolean
  totalOrderAmount: number
  wasRejected: boolean
  hasAdminProducts: boolean
  seatString?: string
}

export interface OrdersParams {
  page?: number
  pageSize?: number
  startDate?: string
  endDate?: string
  searchTerm?: string
  department?: string
  category?: string
  orderNumber?: string
  includeRefunded?: boolean
}

/**
 * Fetch cart by key with enhanced error handling.
 * @param key - The cart key/identifier
 * @returns Promise<Cart> - The cart data
 * @throws CartError for cart-specific errors
 */
export const fetchCart = async (key: string): Promise<Cart> => {
  try {
    // API returns Cart directly (no Result<T> wrapper)
    const cart = await api.get<Cart>(`cart/${key}`).json()

    logger.info('Cart fetched successfully', { cartKey: key, items: cart?.items?.length ?? 0 })

    return cart
  } catch (error: unknown) {
    // Extract more details from the error for better handling
    const apiError = error as Error & {
      response?: { status?: number; statusText?: string }
      status?: number
    }

    const status = apiError?.response?.status || apiError?.status
    const message = apiError?.response?.statusText || apiError?.message || 'Failed to fetch cart items'

    logger.error('Error fetching cart items:', {
      status,
      message,
      cartKey: key,
      error,
    })

    // Handle specific error cases
    if (status === 404) {
      throw new CartError('Cart not found', { isCartNotFound: true })
    }

    // Create an error with status information for retry logic
    const enhancedError = new Error(message, { cause: error }) as Error & {
      status?: number
      response?: { status?: number }
    }
    enhancedError.status = status
    enhancedError.response = { status }

    throw enhancedError
  }
}

/**
 * Create payment token for secure payment processing with comprehensive telemetry
 * @param amount - Payment amount
 * @param invoiceNumber - Optional invoice number
 * @returns Promise<PaymentToken>
 */
export const fetchPaymentToken = async (amount: number, invoiceNumber?: string): Promise<PaymentToken> => {
  const correlationId = paymentTelemetry.generateCorrelationId()
  const startTime = Date.now()

  try {
    // Validate input
    if (amount <= 0) {
      const context: PaymentContext = {
        correlationId,
        processor: 'propay',
        method: 'credit_card',
        amount,
        currency: 'USD',
      }

      const validationError = {
        category: 'validation' as const,
        message: 'Invalid payment amount: must be greater than 0',
        code: 'INVALID_AMOUNT',
        recoverable: false,
      }

      paymentTelemetry.logPaymentError(validationError, context, { amount })
      throw new Error('Invalid payment amount')
    }

    // Create payment context for telemetry
    const paymentContext: PaymentContext = {
      correlationId,
      processor: 'propay',
      method: 'credit_card',
      amount,
      currency: 'USD',
      requestId: `token_${Date.now()}`,
      apiVersion: 'v1',
    }
    // Log payment attempt
    paymentTelemetry.logPaymentEvent('payment.attempt', paymentContext, undefined, {
      'payment.token.request.amount': amount,
      'payment.token.request.invoice_number': invoiceNumber || '',
      'payment.token.request.timestamp': startTime,
    })

    const payload = {
      amount,
      invoiceNumber,
      description: 'PayK12 Payment',
      correlationId, // Include correlation ID in request
    }

    const data = await api.post<Result<PaymentToken>>('payments/token', { json: payload }).json()

    if (!data.isSuccess) {
      const errorMessage = data.errors?.join(', ') || 'Failed to create payment token'

      // Record telemetry for token creation failure
      const tokenError = {
        category: 'validation' as const,
        message: errorMessage,
        code: 'TOKEN_CREATION_FAILED',
        recoverable: false,
        httpStatus: 400,
      }

      paymentTelemetry.logPaymentError(tokenError, paymentContext, { errorType: 'validation' })

      const error = new Error(errorMessage) as Error & { status: number }
      error.status = 400
      throw error
    }

    if (!data.value) {
      const missingDataError = {
        category: 'system' as const,
        message: 'Payment token data is missing from response',
        code: 'MISSING_TOKEN_DATA',
        recoverable: false,
        httpStatus: 500,
      }

      paymentTelemetry.logPaymentError(missingDataError, paymentContext, { errorType: 'processing' })

      const error = new Error('Payment token data is missing from response') as Error & { status: number }
      error.status = 500
      throw error
    }

    // Record successful token creation
    const processingTime = Date.now() - startTime
    paymentTelemetry.logPaymentEvent('payment.success', paymentContext, undefined, {
      'payment.token.success': true,
      'payment.token.session_id': data.value.sessionTokenId,
      'payment.token.expires_in': data.value.expiresIn,
      'payment.token.processing_time_ms': processingTime,
    })

    logger.info('Payment token created successfully', {
      correlationId,
      sessionTokenId: data.value.sessionTokenId,
      expiresIn: data.value.expiresIn,
      processingTimeMs: processingTime,
    })

    return data.value
  } catch (error: unknown) {
    const processingTime = Date.now() - startTime
    const apiError = error as Error & {
      response?: { status?: number }
      status?: number
    }

    const status = apiError?.response?.status || apiError?.status || 500

    // Enhanced error logging with telemetry
    logger.error('Error fetching payment token:', {
      correlationId,
      amount,
      invoiceNumber,
      error,
      processingTimeMs: processingTime,
      httpStatus: status,
    })

    // Record payment error telemetry
    const paymentContext: PaymentContext = {
      correlationId,
      processor: 'propay',
      method: 'credit_card',
      amount,
      currency: 'USD',
    }

    const paymentError = {
      category: status >= 500 ? ('system' as const) : ('network' as const),
      message: apiError.message || 'Failed to fetch payment token',
      recoverable: status >= 500 || status === 408,
      httpStatus: status,
      responseTimeMs: processingTime,
    }

    paymentTelemetry.logPaymentError(paymentError, paymentContext, {
      'payment.token.error': true,
      'payment.token.error.status': status,
      'payment.token.processing_time_ms': processingTime,
    })

    // Use consistent error handling pattern like other commerce API functions
    let message: string
    switch (status) {
      case 401:
        message = 'Authentication required. Please log in.'
        break
      case 429:
        message = 'Too many requests. Please try again later.'
        break
      case 500:
        message = 'Server error. Please try again later.'
        break
      default:
        message = 'Failed to fetch payment token'
        break
    }

    // Create an error with status information for retry logic, consistent with fetchCart
    const enhancedError = new Error(message, { cause: error }) as Error & {
      status?: number
      response?: { status?: number }
    }
    enhancedError.status = status
    enhancedError.response = { status }

    throw enhancedError
  }
}

/**
 * Update cart payment method
 * @param payload - Payment method update request
 * @returns Promise<boolean> - Success status
 */
export const updateCartPaymentMethod = async (payload: UpdateCartPaymentMethodRequest): Promise<boolean> => {
  try {
    // Validate payload
    if (!payload.key) {
      throw new Error('Cart key is required')
    }

    await api.post(`cart/${payload.key}/payment-method`, { json: payload })

    logger.info('Cart payment method updated successfully', {
      cartKey: payload.key,
    })

    return true
  } catch (error: unknown) {
    logger.error('Error updating cart payment method:', {
      cartKey: payload.key,
      error,
    })

    const apiError = error as Error & {
      response?: { status?: number }
      status?: number
    }

    const status = apiError?.response?.status || apiError?.status
    if (status === 404) {
      throw new Error('Cart not found')
    }

    throw new Error('Failed to update cart payment method', { cause: error })
  }
}

/**
 * Fetch orders with pagination and filtering
 * @param params - Query parameters for filtering orders
 * @returns Promise<PagedResponse<Order>>
 */
export const fetchOrders = async (params?: OrdersParams): Promise<PagedResponse<Order>> => {
  try {
    const searchParams = new URLSearchParams()

    // Build query parameters
    if (params?.page) searchParams.append('page', params.page.toString())
    if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString())
    if (params?.startDate) searchParams.append('startDate', params.startDate)
    if (params?.endDate) searchParams.append('endDate', params.endDate)
    if (params?.searchTerm) searchParams.append('searchTerm', params.searchTerm)
    if (params?.department) searchParams.append('department', params.department)
    if (params?.category) searchParams.append('category', params.category)
    if (params?.orderNumber) searchParams.append('orderNumber', params.orderNumber)
    if (params?.includeRefunded !== undefined) {
      searchParams.append('includeRefunded', params.includeRefunded.toString())
    }

    const resp = await api.get<Result<PagedResponse<Order>>>(`marketplace/orders?${searchParams.toString()}`).json()

    if (!resp.isSuccess) {
      throw new Error(resp.errors?.join(', ') || 'Failed to fetch orders')
    }

    if (!resp.value) {
      throw new Error('Orders data is missing from response')
    }

    return resp.value
  } catch (error) {
    logger.error('Error fetching orders:', { params, error })
    throw new Error('Failed to fetch orders', { cause: error })
  }
}

// Secure Payment Processing API
export interface ProcessPaymentRequest {
  sessionTokenId: string
  cartId: string
  paymentType: 'CreditCard' | 'Ach'
  amount: number
  creditCardDetails?: {
    cardHolderName: string
    cardNumber: string
    expirationMonth: number
    expirationYear: number
    cvv: string
    zipCode: string
  }
  achDetails?: {
    nameOnBankAccount: string
    bankAccountNumber: string
    routingNumber: string
    bankAccountType: 'Checking' | 'Savings'
  }
  reCaptchaToken: string
}

export interface ProcessPaymentResponse {
  transactionId: string
  proPayTransactionId: string
  confirmationNumber: string
  processedAt: Date
  amount: number
  lastFour?: string
}

/**
 * Process payment with comprehensive validation, error handling, and telemetry
 * @param request - Payment processing request
 * @returns Promise<ProcessPaymentResponse>
 */
export const processPayment = async (request: ProcessPaymentRequest): Promise<ProcessPaymentResponse> => {
  const correlationId = paymentTelemetry.generateCorrelationId()
  const startTime = Date.now()

  // Create payment context for telemetry (PCI-safe)
  const paymentContext: PaymentContext = {
    correlationId,
    processor: 'propay',
    method: request.paymentType === 'CreditCard' ? 'credit_card' : 'ach',
    amount: request.amount,
    currency: 'USD',
    sessionId: request.sessionTokenId,
    requestId: `payment_${Date.now()}`,
    apiVersion: 'v1',
  }

  // Start payment attempt telemetry
  paymentTelemetry.logPaymentEvent('payment.attempt', paymentContext, undefined, {
    reCaptchaPresent: !!request.reCaptchaToken,
    hasCardDetails: !!request.creditCardDetails,
    hasAchDetails: !!request.achDetails,
  })

  try {
    // Validate request before sending
    validatePaymentRequest(request)

    // Add correlation ID to the request
    const enhancedRequest = {
      ...request,
      correlationId,
      metadata: {
        startTime,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'server',
      },
    }

    const resp = await api.post<Result<ProcessPaymentResponse>>('payments/process', { json: enhancedRequest }).json()

    if (!resp.isSuccess) {
      const errorMessage = resp.errors?.join(', ') || 'Payment processing failed'

      const processingError = {
        category: 'processor' as const,
        message: errorMessage,
        code: 'PAYMENT_PROCESSING_FAILED',
        recoverable: false,
        httpStatus: 400,
      }

      paymentTelemetry.logPaymentError(processingError, paymentContext, {
        errorType: 'processing',
        httpStatus: 400,
      })

      const error = new Error(errorMessage) as Error & { status: number }
      error.status = 400
      throw error
    }

    if (!resp.value) {
      const missingDataError = {
        category: 'system' as const,
        message: 'Payment response data is missing',
        code: 'MISSING_RESPONSE_DATA',
        recoverable: false,
        httpStatus: 500,
      }

      paymentTelemetry.logPaymentError(missingDataError, paymentContext, {
        errorType: 'processing',
        httpStatus: 500,
      })

      const error = new Error('Payment response data is missing') as Error & { status: number }
      error.status = 500
      throw error
    }

    // Record successful payment
    const processingTime = Date.now() - startTime
    paymentTelemetry.logPaymentEvent('payment.success', paymentContext, undefined, {
      transactionId: resp.value.transactionId,
      proPayTransactionId: resp.value.proPayTransactionId,
      confirmationNumber: resp.value.confirmationNumber,
      processingTimeMs: processingTime,
    })

    return resp.value
  } catch (error: unknown) {
    const processingTime = Date.now() - startTime
    const apiError = error as Error & {
      response?: { status?: number }
      status?: number
      message?: string
    }

    const status = apiError?.response?.status || apiError?.status || 500

    // Enhanced error logging
    logger.error('Error processing payment:', {
      correlationId,
      cartId: request.cartId,
      paymentType: request.paymentType,
      processingTimeMs: processingTime,
      httpStatus: status,
      error,
    })

    // Record payment error telemetry if not already recorded
    if (!apiError.message?.includes('Payment processing failed')) {
      const paymentError = {
        category: categorizePaymentError(status, apiError.message),
        message: apiError.message || 'Payment processing failed',
        recoverable: status >= 500 || status === 408,
        httpStatus: status,
        responseTimeMs: processingTime,
      }

      paymentTelemetry.logPaymentError(paymentError, paymentContext, {
        httpStatus: status,
      })
    }

    // Handle specific error cases with user-friendly messages
    switch (status) {
      case 400:
        throw new Error('Invalid payment information. Please check your details.')
      case 401:
        throw new Error('Session expired. Please refresh and try again.')
      case 402:
        throw new Error('Payment declined. Please check your payment details.')
      case 429:
        throw new Error('Too many payment attempts. Please try again later.')
      case 500:
        throw new Error('Payment system error. Please try again later.')
      default:
        throw new Error(apiError?.message || 'Failed to process payment', { cause: error })
    }
  }
}

/**
 * Categorize payment error for telemetry
 */
function categorizePaymentError(
  status?: number,
  message?: string,
): 'validation' | 'processor' | 'timeout' | 'network' | 'system' {
  const msgLower = message?.toLowerCase() || ''

  if (status === 402 || msgLower.includes('declined') || msgLower.includes('insufficient')) {
    return 'processor'
  }
  if (status === 400 || msgLower.includes('invalid') || msgLower.includes('validation')) {
    return 'validation'
  }
  if (status === 408 || msgLower.includes('timeout')) {
    return 'timeout'
  }
  if (msgLower.includes('network') || msgLower.includes('connection')) {
    return 'network'
  }

  return 'system'
}

/**
 * Validate payment request before processing
 * @param request - Payment request to validate
 * @throws Error if validation fails
 */
const validatePaymentRequest = (request: ProcessPaymentRequest): void => {
  if (!request.sessionTokenId) {
    throw new Error('Session token is required')
  }

  if (!request.cartId) {
    throw new Error('Cart ID is required')
  }

  if (!request.reCaptchaToken) {
    throw new Error('Security verification is required')
  }

  if (request.paymentType === 'CreditCard' && !request.creditCardDetails) {
    throw new Error('Credit card details are required')
  }

  if (request.paymentType === 'Ach' && !request.achDetails) {
    throw new Error('Bank account details are required')
  }

  if (request.amount <= 0) {
    throw new Error('Invalid payment amount')
  }
}

// =============================================================================
// Cart API Functions
// =============================================================================

/**
 * Create a new cart or return existing cart for the session.
 * POST /api/v2/cart
 *
 * If a cart already exists for the given sessionId, returns the existing cart.
 * This enables device-based cart persistence across login/logout.
 *
 * @param request - Cart creation request with optional sessionId
 * @returns Promise<CartKeyResponse> - The cart key and ID
 */
export const createCart = async (request: CreateCartRequest): Promise<CartKeyResponse> => {
  try {
    const resp = await api.post<CartKeyResponse>('cart', { json: request }).json()

    // Validate required fields in response
    if (!resp.cartKey || !resp.cartId) {
      logger.error('Invalid cart response - missing required fields', { resp })
      throw new Error('Cart data is missing from response')
    }

    logger.info('Cart created/retrieved successfully', {
      cartKey: resp.cartKey,
      cartId: resp.cartId,
      sessionId: request.sessionId,
    })

    return resp
  } catch (error: unknown) {
    const apiError = error as Error & {
      response?: { status?: number }
      status?: number
    }

    logger.error('Error creating cart:', {
      sessionId: request.sessionId,
      error,
    })

    throw new Error(apiError?.message || 'Failed to create cart', { cause: error })
  }
}

/**
 * Add a Skyward food service payment to the cart.
 * POST /api/v2/cart/skyward-food
 *
 * @param request - Skyward food payment request
 * @returns Promise<AddCartItemResponse>
 */
export const addSkywardFoodItem = async (request: AddSkywardFoodItemRequest): Promise<AddCartItemResponse> => {
  try {
    // Validate required fields
    if (!request.cartKey) {
      throw new Error('Cart key is required')
    }
    if (request.amount <= 0) {
      throw new Error('Amount must be greater than 0')
    }
    if (request.studentId <= 0) {
      throw new Error('Student ID is required')
    }

    // Low-balance frequency requires threshold
    if (request.recurringFrequency === 'low-balance' && !request.lowBalanceThreshold) {
      throw new Error('Low balance threshold is required for low-balance frequency')
    }

    // API returns AddCartItemResponse directly, not wrapped in Result<T>
    const resp = await api.post<AddCartItemResponse>('cart/skyward-food', { json: request }).json()

    // Validate response has required fields (API returns 'id' not 'cartItemId')
    if (!resp || !resp.id) {
      throw new Error('Cart item data is missing from response')
    }

    logger.info('Skyward food item added to cart', {
      cartKey: request.cartKey,
      cartItemId: resp.id,
      amount: request.amount,
    })

    return resp
  } catch (error: unknown) {
    const apiError = error as Error & {
      response?: { status?: number }
      status?: number
    }

    const status = apiError?.response?.status || apiError?.status

    logger.error('Error adding Skyward food item to cart:', {
      cartKey: request.cartKey,
      studentId: request.studentId,
      amount: request.amount,
      error,
    })

    if (status === 404) {
      throw new CartError('Cart not found', { isCartNotFound: true })
    }

    throw new Error(apiError?.message || 'Failed to add Skyward food item to cart', { cause: error })
  }
}

/**
 * Add a Skyward student fee to the cart.
 * POST /api/v2/cart/skyward-fees
 *
 * @param request - Skyward fee request
 * @returns Promise<AddCartItemResponse>
 */
export const addSkywardFeeItem = async (request: AddSkywardFeeItemRequest): Promise<AddCartItemResponse> => {
  try {
    // Validate required fields
    if (!request.cartKey) {
      throw new Error('Cart key is required')
    }
    if (!request.feeId) {
      throw new Error('Fee ID is required')
    }
    if (!request.name) {
      throw new Error('Fee name is required')
    }
    if (request.amount <= 0) {
      throw new Error('Amount must be greater than 0')
    }

    // API returns AddCartItemResponse directly, not wrapped in Result<T>
    const resp = await api.post<AddCartItemResponse>('cart/skyward-fees', { json: request }).json()

    // Validate response has required fields (API returns 'id' not 'cartItemId')
    if (!resp || !resp.id) {
      throw new Error('Cart item data is missing from response')
    }

    logger.info('Skyward fee added to cart', {
      cartKey: request.cartKey,
      cartItemId: resp.id,
      feeId: request.feeId,
      amount: request.amount,
    })

    return resp
  } catch (error: unknown) {
    const apiError = error as Error & {
      response?: { status?: number }
      status?: number
    }

    const status = apiError?.response?.status || apiError?.status

    logger.error('Error adding Skyward fee to cart:', {
      cartKey: request.cartKey,
      feeId: request.feeId,
      amount: request.amount,
      error,
    })

    if (status === 404) {
      throw new CartError('Cart not found', { isCartNotFound: true })
    }

    throw new Error(apiError?.message || 'Failed to add Skyward fee to cart', { cause: error })
  }
}

/**
 * Add a standard merchandise item to the cart.
 * POST /api/v2/cart/items
 *
 * @param request - Standard item request
 * @returns Promise<AddCartItemResponse>
 */
export const addStandardItem = async (request: AddStandardItemRequest): Promise<AddCartItemResponse> => {
  try {
    // Validate required fields
    if (!request.cartKey) {
      throw new Error('Cart key is required')
    }
    if (request.itemId <= 0) {
      throw new Error('Item ID must be greater than 0')
    }

    // Default qty to 1 if not provided
    const payload = {
      ...request,
      qty: request.qty ?? 1,
    }

    // API returns AddCartItemResponse directly, not wrapped in Result<T>
    const resp = await api.post<AddCartItemResponse>('cart/items', { json: payload }).json()

    // Validate response has required fields (API returns 'id' not 'cartItemId')
    if (!resp || !resp.id) {
      throw new Error('Cart item data is missing from response')
    }

    logger.info('Standard item added to cart', {
      cartKey: request.cartKey,
      cartItemId: resp.id,
      itemId: request.itemId,
      qty: payload.qty,
    })

    return resp
  } catch (error: unknown) {
    const apiError = error as Error & {
      response?: { status?: number }
      status?: number
    }

    const status = apiError?.response?.status || apiError?.status

    logger.error('Error adding standard item to cart:', {
      cartKey: request.cartKey,
      itemId: request.itemId,
      qty: request.qty,
      error,
    })

    if (status === 404) {
      throw new CartError('Cart or item not found', { isCartNotFound: true })
    }

    throw new Error(apiError?.message || 'Failed to add item to cart', { cause: error })
  }
}

/**
 * Add an event ticket to the cart.
 * POST /api/v2/cart/events
 *
 * Supports three item types:
 * - 4: General Admission
 * - 5: Reserved Seating (requires seatId)
 * - 6: Event Pass
 *
 * @param request - Event item request
 * @returns Promise<AddCartItemResponse>
 */
export const addEventItem = async (request: AddEventItemRequest): Promise<AddCartItemResponse> => {
  try {
    // Validate required fields
    if (!request.cartKey) {
      throw new Error('Cart key is required')
    }
    if (request.itemId <= 0) {
      throw new Error('Item ID must be greater than 0')
    }
    if (![4, 5, 6].includes(request.itemType)) {
      throw new Error('Item type must be 4 (GeneralAdmission), 5 (ReservedSeating), or 6 (EventPass)')
    }

    // Reserved seating requires seatId
    if (request.itemType === 5 && !request.seatId) {
      throw new Error('Seat ID is required for reserved seating events')
    }

    // Renewal requires oldPassCartItemId
    if (request.renew && !request.oldPassCartItemId) {
      throw new Error('Old pass cart item ID is required for renewals')
    }

    // Default qty to 1 if not provided
    const payload = {
      ...request,
      qty: request.qty ?? 1,
    }

    // API returns AddCartItemResponse directly, not wrapped in Result<T>
    const resp = await api.post<AddCartItemResponse>('cart/events', { json: payload }).json()

    // Validate response has required fields (API returns 'id' not 'cartItemId')
    if (!resp || !resp.id) {
      throw new Error('Cart item data is missing from response')
    }

    logger.info('Event item added to cart', {
      cartKey: request.cartKey,
      cartItemId: resp.id,
      itemId: request.itemId,
      itemType: request.itemType,
      qty: payload.qty,
    })

    return resp
  } catch (error: unknown) {
    const apiError = error as Error & {
      response?: { status?: number }
      status?: number
    }

    const status = apiError?.response?.status || apiError?.status

    logger.error('Error adding event item to cart:', {
      cartKey: request.cartKey,
      itemId: request.itemId,
      itemType: request.itemType,
      error,
    })

    if (status === 404) {
      throw new CartError('Cart or event not found', { isCartNotFound: true })
    }

    throw new Error(apiError?.message || 'Failed to add event to cart', { cause: error })
  }
}

/**
 * Add a variable-price item to the cart.
 * POST /api/v2/cart/variable-pricing
 *
 * Used for items where the customer specifies the amount (e.g., donations).
 *
 * @param request - Variable pricing request
 * @returns Promise<AddCartItemResponse>
 */
export const addVariablePricingItem = async (request: AddVariablePricingItemRequest): Promise<AddCartItemResponse> => {
  try {
    // Validate required fields
    if (!request.cartKey) {
      throw new Error('Cart key is required')
    }
    if (request.itemId <= 0) {
      throw new Error('Item ID must be greater than 0')
    }
    if (request.amount <= 0) {
      throw new Error('Amount must be greater than 0')
    }

    // Default qty to 1 if not provided
    const payload = {
      ...request,
      qty: request.qty ?? 1,
    }

    // API returns AddCartItemResponse directly, not wrapped in Result<T>
    const resp = await api.post<AddCartItemResponse>('cart/variable-pricing', { json: payload }).json()

    // Validate response has required fields (API returns 'id' not 'cartItemId')
    if (!resp || !resp.id) {
      throw new Error('Cart item data is missing from response')
    }

    logger.info('Variable pricing item added to cart', {
      cartKey: request.cartKey,
      cartItemId: resp.id,
      itemId: request.itemId,
      amount: request.amount,
    })

    return resp
  } catch (error: unknown) {
    const apiError = error as Error & {
      response?: { status?: number }
      status?: number
    }

    const status = apiError?.response?.status || apiError?.status

    logger.error('Error adding variable pricing item to cart:', {
      cartKey: request.cartKey,
      itemId: request.itemId,
      amount: request.amount,
      error,
    })

    if (status === 404) {
      throw new CartError('Cart or item not found', { isCartNotFound: true })
    }

    throw new Error(apiError?.message || 'Failed to add variable pricing item to cart', { cause: error })
  }
}
